import { Frame, Landmark, PenTool } from "lucide-react";
import { FishMotif, LotusMotif, PeacockMotif, Reveal, SectionHeading, SunMotif, TreeMotif } from "@/components/motifs";
import { LITERATURE as LITERATURE_TEXT, MOTIFS, STYLES, WORLD_INTRO } from "@/content/chapters";

// The text is shared with the Worker's crawler HTML; icons are matched by title.
const MOTIF_ICONS = { Fish: FishMotif, Lotus: LotusMotif, Peacock: PeacockMotif, "Sun & Moon": SunMotif, "Tree of Life": TreeMotif };
const LITERATURE_ICONS = { "Born on Walls": Landmark, "Twig, Nib & Natural Colour": PenTool, "The Rule of Fullness": Frame };
const GUIDE = MOTIFS.map((m) => ({ ...m, icon: MOTIF_ICONS[m.title] }));
const LITERATURE = LITERATURE_TEXT.map((l) => ({ ...l, icon: LITERATURE_ICONS[l.title] }));

export const WorldChapter = ({ headingAs }) => (
  <section id="world" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="world-section">
    <SectionHeading number="03" title="The World of Mithila" note="A living visual language" as={headingAs} />

    <div className="grid gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <p className="font-serif text-xl leading-relaxed sm:text-2xl">
          {WORLD_INTRO[0]}
        </p>
        <p className="mt-6 max-w-md text-sm leading-relaxed text-muted-foreground">
          {WORLD_INTRO[1]}
        </p>
      </Reveal>

      <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:col-span-8">
        {GUIDE.map((g, i) => (
          <Reveal key={g.title} delay={0.05 * i} className="bg-background">
            <div className="group flex h-full flex-col gap-4 p-7 transition-colors duration-500 hover:bg-paper">
              <g.icon className="h-10 w-10 text-foreground/70 transition-colors duration-500 group-hover:text-accent" />
              <h3 className="font-serif text-xl font-medium tracking-tight">{g.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{g.text}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.25} className="bg-background">
          <div className="flex h-full flex-col justify-between gap-6 p-7">
            <p className="eyebrow text-muted-foreground">Five classical styles</p>
            <ul className="space-y-3">
              {STYLES.map((s) => (
                <li key={s.name} className="flex items-baseline justify-between gap-4">
                  <span className="font-serif text-lg font-medium">{s.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>

    <div className="mt-16 grid gap-px overflow-hidden border border-border bg-border lg:grid-cols-3">
      {LITERATURE.map((l, i) => (
        <Reveal key={l.title} delay={0.07 * i} className="bg-background">
          <div className="group flex h-full flex-col gap-4 p-8 transition-colors duration-500 hover:bg-paper" data-testid={`mithila-literature-${i}`}>
            <l.icon
              size={24}
              strokeWidth={1.4}
              className="text-foreground/60 transition-colors duration-500 group-hover:text-accent"
            />
            <h3 className="font-serif text-xl font-medium tracking-tight sm:text-2xl">{l.title}</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">{l.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);
